// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import {
  type FC,
  type MouseEventHandler,
  type PropsWithChildren,
  type ReactElement,
  cloneElement,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { AppIcon, type SvgIconName } from '@shared/ui-kit/components/app-icon';
import { Menu, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';

type PortfolioActionName =
  | 'Edit'
  | 'Share'
  | 'Delete'
  | 'Analyze'
  | 'Download'
  | 'Trader'
  | 'TaxManager'
  | 'Rebalance'
  | 'Confirm'
  | 'Comparison'
  | 'Buy'
  | 'Sell'
  | 'Transfer';

type PortfolioEventHandlers = `on${Capitalize<PortfolioActionName>}`;

type PortfolioAction = Uncapitalize<PortfolioActionName>;

type PortfolioActions = Partial<
  Record<PortfolioAction, boolean> &
    Record<PortfolioEventHandlers, MouseEventHandler<HTMLElement>> &
    Record<`${PortfolioAction}Wrapper`, ReactElement<PropsWithChildren>>
>;

type PortfolioActionOption = {
  enabled?: boolean;
  text: string;
  onClick?: MouseEventHandler<HTMLElement>;
  icon: SvgIconName;
  wrapper?: ReactElement<PropsWithChildren>;
};

export interface PortfolioActionsMenu extends PortfolioActions {
  organizations?: boolean;
}

export const PortfolioActionsMenu: FC<PortfolioActionsMenu> = ({
  organizations,
  ...props
}) => {
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = useMemo(() => Boolean(anchorEl), [anchorEl]);

  const options = useMemo(
    () =>
      new Map<PortfolioAction, PortfolioActionOption>(
        Object.entries({
          edit: {
            enabled: props.edit,
            text: t('common.edit', { ns: 'common' }),
            onClick: props.onEdit,
            icon: 'Pen',
            wrapper: props.editWrapper,
          },
          share: {
            enabled: props.share,
            text: t('common.share', { ns: 'common' }),
            onClick: props.onShare,
            icon: organizations ? 'OpenLock' : 'Lock',
            wrapper: props.shareWrapper,
          },
          comparison: {
            enabled: props.comparison,
            text: t('common.comparison', { ns: 'common' }),
            onClick: props.onComparison,
            icon: 'Search',
            wrapper: props.comparisonWrapper,
          },
          analyze: {
            enabled: props.analyze,
            text: t('common.analyze', { ns: 'common' }),
            onClick: props.onAnalyze,
            icon: 'Analysis',
            wrapper: props.analyzeWrapper,
          },
          taxManager: {
            enabled: props.taxManager,
            text: t('common.tax-manager', { ns: 'common' }),
            onClick: props.onTaxManager,
            icon: 'TaxManager',
            wrapper: props.taxManagerWrapper,
          },
          rebalance: {
            enabled: props.rebalance,
            text: t('common.rebalance', { ns: 'common' }),
            onClick: props.onRebalance,
            icon: 'Rebalance',
            wrapper: props.rebalanceWrapper,
          },
          trader: {
            enabled: props.trader,
            text: t('common.operations', { ns: 'common' }),
            onClick: props.onTrader,
            icon: 'ShoppingCart',
            wrapper: props.traderWrapper,
          },
          download: {
            enabled: props.download,
            text: t('common.download', { ns: 'common' }),
            onClick: props.onDownload,
            icon: 'Download',
            wrapper: props.downloadWrapper,
          },
          confirm: {
            enabled: props.confirm,
            text: t('common.confirm', { ns: 'common' }),
            onClick: props.onConfirm,
            icon: 'Account',
            wrapper: props.confirmWrapper,
          },
          delete: {
            enabled: props.delete,
            text: t('common.delete', { ns: 'common' }),
            onClick: props.onDelete,
            icon: 'Trashcan',
            wrapper: props.deleteWrapper,
          },
          buy: {
            enabled: props.buy,
            text: t('glossary.kpi-buy', { ns: 'glossary' }),
            onClick: props.onBuy,
            icon: 'CircleArrowUp',
            wrapper: props.buyWrapper,
          },
          sell: {
            enabled: props.sell,
            text: t('glossary.kpi-sell', { ns: 'glossary' }),
            onClick: props.onSell,
            icon: 'CircleArrowDown',
            wrapper: props.sellWrapper,
          },
          transfer: {
            enabled: props.transfer,
            text: t('common.transfer', { ns: 'common' }),
            onClick: props.onTransfer,
            icon: 'ArrowLeftRight',
            wrapper: props.transferWrapper,
          },
        }) as Array<[PortfolioAction, PortfolioActionOption]>,
      ),
    [organizations, props, t],
  );

  const onClick: MouseEventHandler<HTMLButtonElement> = useCallback(event => {
    setAnchorEl(event.currentTarget);
  }, []);

  const onClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const onItemClick = useCallback(
    (option: PortfolioAction): MouseEventHandler<HTMLElement> =>
      event => {
        onClose();

        return options.get(option)?.onClick?.(event);
      },
    [onClose, options],
  );

  const Wrapper: FC<PropsWithChildren<{ option: PortfolioAction }>> = useCallback(
    ({ option, children }) => {
      // eslint-disable-next-line react/jsx-no-useless-fragment
      const wrapper = options.get(option)?.wrapper ?? <></>;

      return cloneElement(wrapper, undefined, children);
    },
    [options],
  );

  return (
    <div
      className="text-border-dark"
      onClick={event => event.stopPropagation()}
      data-testid="portfolio-actions-menu"
    >
      <button
        id="basic-button"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={onClick}
        className="flex items-center justify-center gap-x-2.5"
        data-testid="portfolio-actions"
      >
        <span className="text-sm font-normal leading-6">
          {t('common.actions', { ns: 'common' })}
        </span>
        <AppIcon icon="ArrowDown" color="inherit" size="base" />
      </button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        data-testid="basic-menu"
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {Array.from(options, ([key, option]) =>
          option.enabled ? (
            <MenuItem
              key={key}
              data-testid={`action-${key}`}
              onClick={onItemClick(key)}
            >
              <Wrapper option={key}>
                <div className="flex items-center justify-center gap-x-3">
                  <AppIcon icon={option.icon} size="lg" data-testid={option.icon} />
                  <span>{option.text}</span>
                </div>
              </Wrapper>
            </MenuItem>
          ) : null,
        )}
      </Menu>
    </div>
  );
};
